import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LanguageIcon from "@mui/icons-material/LanguageOutlined";
import LiveIcon from "@mui/icons-material/LiveTvOutlined";
import SlideIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import VolumeOffIcon from "@mui/icons-material/VolumeOffOutlined";
import VolumeUpIcon from "@mui/icons-material/VolumeUpOutlined";
import { tokens } from "../theme";
import AssetsPicker from "./AssetsPicker";
import ChannelMark from "./ChannelMark";
import SignagePreviewPlayer from "./SignagePreviewPlayer";
import {
  foyerApi,
  signageScopeKey,
  type AssetSummary,
  type SignageChannelOption,
  type SignageScopeRef,
  type SignageUrlProbe,
} from "../http/foyer";
import type {
  SignageItem,
  SignageItemFit,
  SignagePlaylist,
  SignagePlaylistPreview,
} from "@shared/types/signage";
import type { SignageTemplateInstance } from "@shared/types/signage-template";
import {
  byUrl,
  displaySeconds,
  itemFromAsset,
  itemFromChannel,
  itemFromSlide,
  itemFromWebUrl,
  loopSeconds,
  reorder,
  thumbnailFor,
  withMeasuredDurations,
} from "../utils/signageItems";
import { ORIENTATION_LABELS, durationLabel } from "@shared/media";
import { signageSaveErrorText } from "../utils/signageRefusal";

/**
 * Create or edit a playlist - the ordered loop a screen plays.
 *
 * A playlist is the unit everything else schedules: day plans drop them onto a
 * timeline, campaigns interrupt them. Until this existed the whole product had
 * a hole in the middle - media could be uploaded and loops could be scheduled,
 * but nothing could turn the first into the second.
 */

/** What an image holds the screen for when nothing says otherwise. */
export const DEFAULT_ITEM_SECONDS = 15;

export interface SignagePlaylistDialogProps {
  open: boolean;
  onClose: () => void;
  /** Absent = creating a new one. */
  playlist?: SignagePlaylist | null;
  /** The library, for thumbnails and clip lengths. */
  assets: AssetSummary[];
  onSave: (patch: Partial<SignagePlaylist>) => Promise<void>;
  saving?: boolean;
  /**
   * Why the last save failed. A string is shown as-is; anything else is the
   * error the save threw, and the dialog turns it into a sentence itself,
   * because only the dialog can name a refused title the way the operator
   * knows it (the channel's name rather than its catalogue id). See
   * `signageSaveErrorText`.
   */
  error?: unknown;
  /**
   * Author a CHANNEL rather than a playlist (`BE-40`).
   *
   * Same record, same editor: a channel IS a global-layer playlist. What
   * changes is the vocabulary on the buttons and the one extra field - a
   * `category`, which is the row a channel appears under in the catalogue and
   * is meaningless on a customer's own loops. Passing the flag rather than
   * building a second dialog is deliberate: the two surfaces drifted apart once
   * already when "Create playlist" named two different things.
   */
  asChannel?: boolean;
  /** Suggestions for the category box - the categories already in use. */
  categories?: string[];
  /**
   * Which signage scope this playlist belongs to.
   *
   * Only used to ask what live TV channels are available here. Absent means the
   * caller has no scope to offer - the channel affordance simply does not
   * appear, which is the right default for any surface that has not thought
   * about it.
   */
  scope?: SignageScopeRef;
}

export default function SignagePlaylistDialog({
  open,
  onClose,
  playlist,
  assets,
  onSave,
  saving,
  error,
  asChannel = false,
  categories = [],
  scope,
}: SignagePlaylistDialogProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  /** The channel's own artwork, absolute. Empty = none of its own - see
   * `channelPosterUrl`, which is what a browsing page falls back to. */
  const [posterUrl, setPosterUrl] = useState("");
  /** The channel's watermark, absolute. Burned into the corner the corner
   * authority grants `channel-logo` (`@shared/tv-corners`) - bottom-left today -
   * placement itself is a single global setting, under General settings. */
  const [logoUrl, setLogoUrl] = useState("");
  const [items, setItems] = useState<SignageItem[]>([]);
  const [defaultSeconds, setDefaultSeconds] = useState(DEFAULT_ITEM_SECONDS);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [posterPickerOpen, setPosterPickerOpen] = useState(false);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const [webOpen, setWebOpen] = useState(false);
  const [channelOpen, setChannelOpen] = useState(false);
  const [slideOpen, setSlideOpen] = useState(false);
  const [slides, setSlides] = useState<SignageTemplateInstance[]>([]);
  const [channels, setChannels] = useState<SignageChannelOption[]>([]);
  /**
   * Real titles + posters for catalogue items - keyed by `contentAssetId`,
   * filled in as they resolve. Absent is not "loading" here; a row simply
   * keeps its generic mark until an entry lands, and forever if one never
   * does (see `HYDRATE_SUMMARIES_LIMIT`).
   */
  const [catalogInfo, setCatalogInfo] = useState<
    Map<string, { title: string; image: string | null }>
  >(new Map());
  const hydratedCatalogIds = useRef(new Set<string>());
  /** An item the operator clicked to look at full-size, or `null` for closed. */
  const [previewItem, setPreviewItem] = useState<SignageItem | null>(null);
  // A catalogue title's preview is a server round trip (`contentAssetId` has
  // no URL of its own); a file's is not, so these stay empty for that case.
  const [catalogPreview, setCatalogPreview] =
    useState<SignagePlaylistPreview | null>(null);
  const [catalogPreviewLoading, setCatalogPreviewLoading] = useState(false);
  const [catalogPreviewError, setCatalogPreviewError] = useState<string | null>(
    null
  );
  /**
   * Was live TV sold for this place?
   *
   * Drives whether the button exists at all. A control that opens a dialog to
   * say "you cannot do this" is worse than no control - it advertises an
   * upsell in the middle of somebody's work. The dialog is only reachable when
   * there is something behind it.
   */
  const [liveTvEnabled, setLiveTvEnabled] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(playlist?.name ?? "");
    setCategory(playlist?.category ?? "");
    setPosterUrl(playlist?.posterUrl ?? "");
    setLogoUrl(playlist?.logoUrl ?? "");
    // Repair clip lengths on the way in - see `withMeasuredDurations`.
    setItems(withMeasuredDurations(playlist?.items ?? [], byUrl(assets)));
    setDefaultSeconds(playlist?.defaultItemSeconds || DEFAULT_ITEM_SECONDS);
    setSoundEnabled(playlist?.soundEnabled ?? false);
    // A fresh dialog must not inherit the last one's preview, or the last
    // channel's resolved titles and posters.
    setPreviewItem(null);
    setCatalogInfo(new Map());
    hydratedCatalogIds.current.clear();
  }, [open, playlist, assets]);

  /*
   * What live TV this scope can offer.
   *
   * A catalogue read, so it is allowed to fail without taking the editor with
   * it: somebody arranging pictures must not be blocked because the Content API
   * is having a moment. On failure the button simply is not there - the same
   * outcome as "not sold", which is a shame but not a lie, and far better than
   * a button that opens onto an error.
   */
  const scopeKind = scope?.kind;
  const scopeIdent = scope ? signageScopeKey(scope) : "";
  /**
   * Whether `channels` reflects a real answer yet - true immediately when
   * there is no scope to ask at all, since nothing will ever arrive.
   *
   * Read by the catalogue-hydration effect below: an item's `contentAssetId`
   * might name a real live TV channel, and only `channels` can say so. Without
   * this, the two fetches race, and a real channel can lose its broadcaster
   * mark to a content-catalog poster that happens to share its id - not a
   * flicker, since nothing ever clears a resolved `catalogInfo` entry.
   */
  const [channelsSettled, setChannelsSettled] = useState(!scope);
  useEffect(() => {
    if (!open || !scope) return;
    setChannelsSettled(false);
    let alive = true;
    foyerApi
      .listSignageChannelOptionsIn(scope)
      .then((res) => {
        if (!alive) return;
        setChannels(res.channels);
        setLiveTvEnabled(res.liveTvEnabled && res.channels.length > 0);
      })
      .catch(() => {
        if (!alive) return;
        setChannels([]);
        setLiveTvEnabled(false);
      })
      .finally(() => {
        if (alive) setChannelsSettled(true);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeKind, scopeIdent]);

  /*
   * The slides this scope can schedule.
   *
   * Allowed to fail quietly like the channel read: somebody arranging pictures
   * must not be blocked because one list did not load. The button simply is not
   * there, which is the same outcome as "none built yet".
   */
  useEffect(() => {
    if (!open || !scope) return;
    let alive = true;
    foyerApi
      .listSignageSlidesIn(scope)
      .then((list) => alive && setSlides(list))
      .catch(() => alive && setSlides([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeKind, scopeIdent]);

  /**
   * Measure clips the library has no length for.
   *
   * The uploader records a duration, but a clip from before that - or one whose
   * container the browser could not length at upload time - arrives with none,
   * and lands here as a 0 that means "we do not know". Rather than making an
   * operator stopwatch their own file, ask the browser again now: the URL is
   * seekable and `preload="metadata"` fetches only the header.
   *
   * Attempts are remembered so a clip that genuinely cannot be measured is
   * tried once, not on every render.
   */
  const measuring = useRef(new Set<string>());
  useEffect(() => {
    const pending = items.filter(
      (it) =>
        it.kind === "video" &&
        it.durationSeconds <= 0 &&
        !measuring.current.has(it.assetUrl)
    );
    if (pending.length === 0) return;

    let alive = true;
    pending.forEach((it) => {
      measuring.current.add(it.assetUrl);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        const d = v.duration;
        if (!alive || !Number.isFinite(d) || d <= 0) return;
        setItems((prev) =>
          prev.map((x) =>
            x.assetUrl === it.assetUrl && x.durationSeconds <= 0
              ? { ...x, durationSeconds: Math.ceil(d) }
              : x
          )
        );
      };
      v.src = it.assetUrl;
    });
    return () => {
      alive = false;
    };
  }, [items]);

  // A fresh dialog must not inherit the last one's measurement attempts.
  useEffect(() => {
    if (!open) measuring.current.clear();
  }, [open]);

  /**
   * Real titles + posters for the items already IN this loop - unlike the
   * clip preview below, fetched up front rather than on click: the question
   * a row's tiny mark leaves unanswered is "what even is this", and an
   * operator scanning a channel is asking it about every row, not one.
   *
   * Bounded all the same (`HYDRATE_SUMMARIES_LIMIT`, server-side): a channel
   * like "Thrill Seekers TV" holds 355 catalogue ids, and this dialog has no
   * pagination on the item list to only ask about what is actually visible.
   *
   * Waits for `channelsSettled`: a real live TV channel names itself through
   * `channels`, and asking the content catalogue about an id before knowing
   * that would risk a broadcaster's row keeping a content-catalog poster
   * instead of its own mark - see `channelsSettled`'s own doc comment.
   */
  useEffect(() => {
    if (!open || !channelsSettled) return;
    const need = [
      ...new Set(
        items
          .filter(
            (it) =>
              it.contentAssetId &&
              !channels.some((c) => c.contentAssetId === it.contentAssetId) &&
              !hydratedCatalogIds.current.has(it.contentAssetId)
          )
          .map((it) => it.contentAssetId!)
      ),
    ];
    if (need.length === 0) return;
    need.forEach((id) => hydratedCatalogIds.current.add(id));
    let alive = true;
    foyerApi
      .hydrateSignageContentAssets(need)
      .then((res) => {
        if (!alive) return;
        setCatalogInfo((prev) => {
          const next = new Map(prev);
          for (const r of res.items) {
            next.set(r.assetId, { title: r.title, image: r.image });
          }
          return next;
        });
      })
      .catch(() => {
        // A row that would not resolve keeps its generic mark - not worth
        // retrying every render for.
      });
    return () => {
      alive = false;
    };
  }, [open, items, channels, channelsSettled]);

  /*
   * Resolve a catalogue item's clip on demand, the moment its row is clicked -
   * never up front. `contentAssetId` has no URL of its own; asking the
   * catalogue for all 355 of them to fill a list of rows nobody has clicked
   * yet is exactly the read `signageStore.playlistPreview`'s own doc comment
   * warns against.
   */
  useEffect(() => {
    if (
      !previewItem ||
      previewItem.assetUrl ||
      !previewItem.contentAssetId ||
      !playlist?.id
    ) {
      setCatalogPreview(null);
      setCatalogPreviewError(null);
      return;
    }
    let alive = true;
    setCatalogPreviewLoading(true);
    setCatalogPreviewError(null);
    foyerApi
      .signagePlaylistPreview(playlist.id, previewItem.id)
      .then((p) => alive && setCatalogPreview(p))
      .catch(() => alive && setCatalogPreviewError("Could not load this clip."))
      .finally(() => alive && setCatalogPreviewLoading(false));
    return () => {
      alive = false;
    };
  }, [previewItem, playlist?.id]);

  const library = useMemo(() => byUrl(assets), [assets]);
  const total = loopSeconds(items, defaultSeconds, library);
  /** Nothing uses the playlist default when every item is a clip. */
  const usesDefault = items.some((it) => it.kind !== "video");

  const patch = (i: number, change: Partial<SignageItem>) =>
    setItems((prev) =>
      prev.map((x, xi) => (xi === i ? { ...x, ...change } : x))
    );

  const addAsset = (asset: AssetSummary) => {
    setItems((prev) => [
      ...prev,
      itemFromAsset(asset, prev.length, defaultSeconds),
    ]);
    setPickerOpen(false);
  };

  const addWebPage = (url: string) => {
    setItems((prev) => [
      ...prev,
      itemFromWebUrl(url, prev.length, defaultSeconds),
    ]);
    setWebOpen(false);
  };

  /*
   * What a live channel will actually do on the wall - said here because the
   * difference is invisible in the editor and otherwise discovered on a wall.
   *
   * `playheadAt` picks an item by taking the time modulo the loop's length, so
   * a loop of ONE item returns that item forever: a channel alone runs until
   * the schedule moves on, and its seconds box changes nothing. In a MIXED loop
   * it takes its turn like anything else, holding the screen for its slot
   * length (the durationSeconds ruling - "hold the screen this long" - see
   * `itemFromChannel`).
   *
   * "Live" means a broadcast the picker knows, or one the resolver already
   * marked `live`. A venue clip also carries a `contentAssetId` but has a
   * natural end, so it is not a channel for this purpose.
   */
  const liveCount = items.filter(
    (it) =>
      it.live === true ||
      (Boolean(it.contentAssetId) &&
        channels.some((c) => c.contentAssetId === it.contentAssetId))
  ).length;
  const liveHint =
    liveCount === 0
      ? null
      : items.length === 1
        ? "A channel on its own plays continuously until the schedule changes - its seconds are not used. Add other items and it takes turns with them instead."
        : `${liveCount === 1 ? "The channel takes" : "Channels take"} turns with the other items: each holds the screen for its seconds, then the loop moves on. Keep a channel on its own in a playlist to show it continuously.`;

  const addChannel = (c: SignageChannelOption) => {
    setItems((prev) => [
      ...prev,
      itemFromChannel(c, prev.length, defaultSeconds),
    ]);
    setChannelOpen(false);
  };

  const addSlide = (slide: SignageTemplateInstance) => {
    setItems((prev) => [
      ...prev,
      itemFromSlide(slide, prev.length, defaultSeconds),
    ]);
    setSlideOpen(false);
  };

  const canSave = name.trim().length > 0 && items.length > 0 && !saving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 4 }}>
        <Stack direction="row" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: 22, fontWeight: 700, color: tokens.ink }}
            >
              {asChannel
                ? playlist
                  ? "Edit channel"
                  : "New channel"
                : playlist
                  ? "Edit playlist"
                  : "New playlist"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
              {asChannel
                ? "A curated loop every customer can schedule. Items run top to bottom, then repeat."
                : "The loop a screen plays. Items run top to bottom, then repeat."}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack gap={3}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ "data-testid": "playlist-name" }}
            />
            {asChannel && (
              <TextField
                size="small"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                sx={{ width: { xs: "100%", sm: 260 } }}
                // Free text with suggestions rather than a fixed list: the
                // catalogue's rows ARE its categories, so a closed vocabulary
                // would need a second admin surface to grow one.
                inputProps={{
                  list: "signage-channel-categories",
                  "data-testid": "channel-category",
                }}
                helperText="Groups it in the catalogue"
              />
            )}
            {asChannel && (
              <datalist id="signage-channel-categories">
                {[...new Set(categories)].sort().map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            )}
          </Stack>

          {/*
            The channel's own artwork - `BE-40` again: a playlist has never
            needed a picture of itself, since the schedule builder shows its
            items, but a CATALOGUE row is browsed cold and needs something to
            show before anyone has clicked in. Only meaningful here, same
            reasoning as `category` right above.
          */}
          {asChannel && (
            <Stack direction="row" gap={2} alignItems="center">
              <Box
                sx={{
                  width: 120,
                  height: 80,
                  flexShrink: 0,
                  borderRadius: "8px",
                  border: `1px solid ${tokens.divider}`,
                  bgcolor: tokens.rowAlt,
                  overflow: "hidden",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {posterUrl ? (
                  <Box
                    component="img"
                    src={posterUrl}
                    alt=""
                    data-testid="channel-poster-preview"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <MovieIcon sx={{ fontSize: 22, color: tokens.inkFaint }} />
                )}
              </Box>
              <Stack gap={0.5}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Channel poster
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                  Shown in the catalogue. Without one, the first picture in the
                  loop stands in.
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button
                    size="small"
                    data-testid="choose-poster"
                    onClick={() => setPosterPickerOpen(true)}
                  >
                    {posterUrl ? "Change poster" : "Choose poster"}
                  </Button>
                  {posterUrl && (
                    <Button
                      size="small"
                      color="inherit"
                      data-testid="remove-poster"
                      onClick={() => setPosterUrl("")}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Stack>
          )}

          {/*
            The channel's watermark - burned into the video overlay, bottom-
            right, on every TV that plays this channel. Distinct from the
            poster above: that one is browsed cold in the catalogue and never
            reaches the wall; this one is never shown in the catalogue and
            ONLY reaches the wall. Placement (the corner is fixed) is a single
            global setting, not per-channel - see General settings.
          */}
          {asChannel && (
            <Stack direction="row" gap={2} alignItems="center">
              <Box
                sx={{
                  width: 120,
                  height: 80,
                  flexShrink: 0,
                  borderRadius: "8px",
                  border: `1px solid ${tokens.divider}`,
                  bgcolor: tokens.rowAlt,
                  overflow: "hidden",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {logoUrl ? (
                  <Box
                    component="img"
                    src={logoUrl}
                    alt=""
                    data-testid="channel-logo-preview"
                    sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <MovieIcon sx={{ fontSize: 22, color: tokens.inkFaint }} />
                )}
              </Box>
              <Stack gap={0.5}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Channel logo
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                  Shown as a watermark on the video, in the corner the TV grants
                  it. Placement is set once for every channel, under General
                  settings.
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button
                    size="small"
                    data-testid="choose-logo"
                    onClick={() => setLogoPickerOpen(true)}
                  >
                    {logoUrl ? "Change logo" : "Choose logo"}
                  </Button>
                  {logoUrl && (
                    <Button
                      size="small"
                      color="inherit"
                      data-testid="remove-logo"
                      onClick={() => setLogoUrl("")}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Stack>
          )}

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                Media{" "}
                {items.length > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 13,
                      color: tokens.inkMuted,
                      fontWeight: 400,
                    }}
                  >
                    · {items.length} {items.length === 1 ? "item" : "items"} ·{" "}
                    {durationLabel(total) ?? "00:00"} per loop
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" gap={0.5}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setPickerOpen(true)}
                >
                  Add media
                </Button>
                <Button
                  size="small"
                  startIcon={<LanguageIcon />}
                  onClick={() => setWebOpen(true)}
                >
                  Add web page
                </Button>
                {/* Only where live TV was actually sold AND there is something
                    to pick. See `liveTvEnabled`. */}
                {liveTvEnabled && (
                  <Button
                    size="small"
                    startIcon={<LiveIcon />}
                    onClick={() => setChannelOpen(true)}
                  >
                    Add channel
                  </Button>
                )}
                {/* Only where somebody has actually built one. A button opening
                    an empty picker is an advert for a feature, not a control. */}
                {slides.length > 0 && (
                  <Button
                    size="small"
                    startIcon={<SlideIcon />}
                    onClick={() => setSlideOpen(true)}
                  >
                    Add slide
                  </Button>
                )}
              </Stack>
            </Stack>
            {/* The button above only exists once a slide has been built, so
                without a word here its absence reads as "this can't be done".
                Say where slides come from instead. */}
            {slides.length === 0 && (
              <Typography
                sx={{ fontSize: 12, color: tokens.inkMuted, mt: -0.5, mb: 1 }}
              >
                Want a designed slide in this loop? Build one under Signage →
                Templates → Slides first; it then shows up here to add.
              </Typography>
            )}

            {items.length === 0 ? (
              <Box
                sx={{
                  border: `1px dashed ${tokens.divider}`,
                  borderRadius: "10px",
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                  A playlist needs at least one image, clip or web page.
                </Typography>
              </Box>
            ) : (
              <Stack gap={1}>
                {items.map((it, i) => {
                  const asset = library.get(it.assetUrl);
                  const thumb = thumbnailFor(asset);
                  const isVideo = it.kind === "video";
                  const isWeb = it.kind === "url";
                  // A channel item stores only its catalogue id; the playable
                  // URL is resolved at build time and never saved.
                  const channel = it.contentAssetId
                    ? channels.find(
                        (c) => c.contentAssetId === it.contentAssetId
                      )
                    : undefined;
                  // A broadcaster's mark belongs on a broadcast, not on every
                  // catalogue title. See `SignageItem.live`.
                  const isChannel = Boolean(it.live ?? it.contentAssetId);
                  // The catalogue's own idea of this title - only populated for
                  // ids `channel` above did not already resolve, i.e. an effect
                  // channel's own clips rather than a broadcast. See
                  // `catalogInfo`.
                  const catalogItem = it.contentAssetId
                    ? catalogInfo.get(it.contentAssetId)
                    : undefined;
                  const slide = it.templateInstanceId
                    ? slides.find((x) => x.id === it.templateInstanceId)
                    : undefined;
                  const isSlide = it.kind === "template";
                  const shown = displaySeconds(it, defaultSeconds, asset);
                  // A web page and a slide preview themselves wherever they
                  // already live (the browser, the template editor). A file
                  // in OUR library previews directly, no server round trip
                  // needed. A catalogue title (`isChannel`) needs one - the
                  // playable URL is resolved server-side from
                  // `contentAssetId` - and that round trip is only possible
                  // once the channel has an id to ask about, i.e. it is
                  // already saved. See `SignagePreviewPlayer`.
                  const isFilePreviewable =
                    !isWeb && !isSlide && !isChannel && Boolean(it.assetUrl);
                  const isCatalogPreviewable =
                    isChannel &&
                    Boolean(it.contentAssetId) &&
                    Boolean(playlist?.id);
                  const isPreviewable =
                    isFilePreviewable || isCatalogPreviewable;
                  return (
                    <Stack
                      key={it.id}
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                      sx={{
                        p: 1,
                        border: `1px solid ${tokens.divider}`,
                        borderRadius: "8px",
                      }}
                    >
                      <Stack>
                        <IconButton
                          size="small"
                          aria-label="Move up"
                          disabled={i === 0}
                          onClick={() =>
                            setItems((prev) => reorder(prev, i, i - 1))
                          }
                          sx={{ p: 0.25 }}
                        >
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Move down"
                          disabled={i === items.length - 1}
                          onClick={() =>
                            setItems((prev) => reorder(prev, i, i + 1))
                          }
                          sx={{ p: 0.25 }}
                        >
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>

                      <Box
                        role={isPreviewable ? "button" : undefined}
                        tabIndex={isPreviewable ? 0 : undefined}
                        aria-label={
                          isPreviewable
                            ? `Preview ${
                                asset?.originalName ??
                                channel?.name ??
                                it.contentAssetId ??
                                "media"
                              }`
                            : undefined
                        }
                        onClick={
                          isPreviewable ? () => setPreviewItem(it) : undefined
                        }
                        onKeyDown={
                          isPreviewable
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setPreviewItem(it);
                                }
                              }
                            : undefined
                        }
                        sx={{
                          width: 56,
                          height: 40,
                          flexShrink: 0,
                          borderRadius: "6px",
                          bgcolor: tokens.rowAlt,
                          overflow: "hidden",
                          display: "grid",
                          placeItems: "center",
                          ...(isPreviewable
                            ? {
                                cursor: "pointer",
                                "&:hover": { opacity: 0.85 },
                                "&:focus-visible": {
                                  outline: `2px solid ${tokens.brand}`,
                                  outlineOffset: 1,
                                },
                              }
                            : {}),
                        }}
                      >
                        {/* A clip shows its captured poster. Its own URL in an
                            <img> renders nothing, which is what made these rows
                            look empty. A RESOLVED broadcaster (`channel`)
                            always keeps its own mark - checked ahead of a
                            catalogue title's poster on purpose, so an id that
                            turns out to be a real live TV channel can never
                            get stuck showing a content-catalog picture
                            instead. */}
                        {thumb ? (
                          <Box
                            component="img"
                            src={thumb}
                            alt=""
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : isWeb ? (
                          <LanguageIcon
                            sx={{ fontSize: 18, color: tokens.inkFaint }}
                          />
                        ) : isSlide ? (
                          <SlideIcon
                            sx={{ fontSize: 18, color: tokens.inkFaint }}
                          />
                        ) : channel ? (
                          <ChannelMark
                            src={channel.logoUrl}
                            name={channel.name}
                            id={it.contentAssetId ?? it.id}
                            size={40}
                          />
                        ) : catalogItem?.image ? (
                          <Box
                            component="img"
                            src={catalogItem.image}
                            alt=""
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : isChannel ? (
                          <ChannelMark
                            src={undefined}
                            name={
                              catalogItem?.title ?? it.contentAssetId ?? "?"
                            }
                            id={it.contentAssetId ?? it.id}
                            size={40}
                          />
                        ) : (
                          <MovieIcon
                            sx={{ fontSize: 18, color: tokens.inkFaint }}
                          />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{ fontSize: 13, color: tokens.ink }}
                        >
                          {isWeb
                            ? it.webUrl
                            : isSlide
                              ? // A slide deleted out from under the playlist
                                // still has to render as something an operator
                                // recognises, so the id is the fallback.
                                (slide?.name ?? "Deleted slide")
                              : isChannel
                                ? // A channel the picker no longer lists, or a
                                  // catalogue title not yet resolved, still has
                                  // to render as something an operator
                                  // recognises, so the id is the last resort
                                  // rather than a blank.
                                  (channel?.name ??
                                  catalogItem?.title ??
                                  it.contentAssetId)
                                : (asset?.originalName ??
                                  it.assetUrl.split("/").pop())}
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={0.75}>
                          <Typography
                            sx={{ fontSize: 12, color: tokens.inkMuted }}
                          >
                            {/* The length lives in the column on the right, so
                                this says what the file IS, not how long it runs. */}
                            {isWeb
                              ? "Web page"
                              : isSlide
                                ? "Slide"
                                : isChannel
                                  ? `Live TV${channel?.providerName ? ` · ${channel.providerName}` : ""}`
                                  : isVideo
                                    ? "Clip"
                                    : "Image"}
                            {!isWeb && !isChannel && asset?.orientation
                              ? ` · ${ORIENTATION_LABELS[asset.orientation]}`
                              : ""}
                          </Typography>
                          {/*
                            The standing rule made visible.
                            A fielded legacy autoplayer's wire contract has
                            exactly two asset types, so it cannot render a page
                            and the facade drops the item rather than sending a
                            type the player has no branch for. An operator at a
                            part-migrated site would otherwise build a loop, see
                            it on the new panels, and never learn why the old
                            screens in the same lobby are showing something
                            else.
                          */}
                          {isWeb && (
                            <Chip
                              size="small"
                              label="Foyer screens only"
                              title="Older autoplayer screens cannot show a web page, and will skip this item."
                              sx={{
                                height: 18,
                                fontSize: 11,
                                bgcolor: tokens.rowAlt,
                                color: tokens.inkMuted,
                              }}
                            />
                          )}
                        </Stack>
                      </Box>

                      {/* `fit` crops or letterboxes a picture inside its
                          frame. A web page lays ITSELF out to whatever viewport
                          it is given, so there is no second answer to offer -
                          and a control that changes nothing is worse than none. */}
                      {!isWeb && !isChannel && !isSlide && (
                        <TextField
                          select
                          size="small"
                          label="Fit"
                          value={it.fit}
                          onChange={(e) =>
                            patch(i, { fit: e.target.value as SignageItemFit })
                          }
                          sx={{ width: 116 }}
                        >
                          <MenuItem value="cover">Fill</MenuItem>
                          <MenuItem value="contain">Fit</MenuItem>
                        </TextField>
                      )}

                      {/* A measured clip holds the screen for exactly as long
                          as it runs, so there is nothing here to set. An
                          UNMEASURED one still needs a number - nothing knows
                          its length - so it keeps the box.

                          A CHANNEL is `kind: "video"` too but has no length at
                          all: it is live, and it would hold the screen forever
                          given the chance. Its number is how long the loop
                          lends it the wall before moving on, so it always keeps
                          the editable box. */}
                      {isVideo &&
                      !isChannel &&
                      !isSlide &&
                      it.durationSeconds > 0 ? (
                        <Box sx={{ width: 104, textAlign: "center" }}>
                          <Typography sx={{ fontSize: 13, color: tokens.ink }}>
                            {durationLabel(shown) ?? "—"}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 11, color: tokens.inkMuted }}
                          >
                            Full clip
                          </Typography>
                        </Box>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          label="Seconds"
                          value={it.durationSeconds}
                          onChange={(e) =>
                            patch(i, {
                              durationSeconds: Math.max(
                                1,
                                Number(e.target.value) || 1
                              ),
                            })
                          }
                          sx={{ width: 104 }}
                        />
                      )}

                      {isVideo && (
                        <IconButton
                          size="small"
                          aria-label={it.muted ? "Unmute" : "Mute"}
                          onClick={() => patch(i, { muted: !it.muted })}
                        >
                          {it.muted ? (
                            <VolumeOffIcon fontSize="small" />
                          ) : (
                            <VolumeUpIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}

                      <IconButton
                        size="small"
                        aria-label="Remove media"
                        onClick={() =>
                          setItems((prev) => prev.filter((_, xi) => xi !== i))
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  );
                })}
              </Stack>
            )}
            {liveHint && (
              <Alert
                severity="info"
                data-testid="live-channel-hint"
                sx={{ mt: 1, fontSize: 12 }}
              >
                {liveHint}
              </Alert>
            )}
          </Box>

          <Stack direction="row" gap={3} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              type="number"
              label="Default seconds"
              value={defaultSeconds}
              onChange={(e) =>
                setDefaultSeconds(Math.max(1, Number(e.target.value) || 1))
              }
              // A clip holds the screen for its own length, so a loop made only
              // of clips has nothing for this to apply to.
              disabled={items.length > 0 && !usesDefault}
              sx={{ width: 170 }}
              helperText={
                items.length > 0 && !usesDefault
                  ? "Clips use their own length"
                  : "New images start here"
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={soundEnabled}
                  onChange={(e) => setSoundEnabled(e.target.checked)}
                />
              }
              label="Sound"
            />
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, flex: 1 }}>
              {soundEnabled
                ? "Clips play with sound unless muted individually."
                : "The whole loop is silent - the usual choice for a lobby."}
            </Typography>
          </Stack>

          {Boolean(error) && (
            <Typography
              data-testid="playlist-save-error"
              sx={{ fontSize: 13, color: tokens.error }}
            >
              {typeof error === "string"
                ? error
                : signageSaveErrorText(
                    error,
                    asChannel
                      ? "Could not save the channel. Try again."
                      : "Could not save the playlist. Try again.",
                    (assetId) =>
                      channels.find((c) => c.contentAssetId === assetId)
                        ?.name ?? catalogInfo.get(assetId)?.title
                  )}
            </Typography>
          )}

          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canSave}
              onClick={() =>
                void onSave({
                  name: name.trim(),
                  items,
                  defaultItemSeconds: defaultSeconds,
                  soundEnabled,
                  // Only sent when this IS a channel: the two lower scopes
                  // refuse the field, and sending an empty one at them would
                  // read as "clear the category" on a record that has none.
                  ...(asChannel
                    ? {
                        category: category.trim(),
                        posterUrl: posterUrl.trim(),
                        logoUrl: logoUrl.trim(),
                      }
                    : {}),
                })
              }
            >
              {saving
                ? "Saving…"
                : playlist
                  ? "Save"
                  : asChannel
                    ? "Create channel"
                    : "Create playlist"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>

      <AssetsPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(_url, asset) => addAsset(asset)}
      />
      <AssetsPicker
        open={posterPickerOpen}
        onClose={() => setPosterPickerOpen(false)}
        onSelect={(url) => {
          setPosterUrl(url);
          setPosterPickerOpen(false);
        }}
      />
      <AssetsPicker
        open={logoPickerOpen}
        onClose={() => setLogoPickerOpen(false)}
        onSelect={(url) => {
          setLogoUrl(url);
          setLogoPickerOpen(false);
        }}
      />
      {/*
        A full-size look at one item. Not the same surface as the AssetsPicker
        above - that browses a whole library to ADD something; this shows the
        one file already IN the loop, which is the question an operator
        actually has when a row's tiny 56×40 thumbnail is ambiguous.
      */}
      <Dialog
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>
            {!previewItem
              ? ""
              : previewItem.assetUrl
                ? (library.get(previewItem.assetUrl)?.originalName ??
                  previewItem.assetUrl.split("/").pop())
                : ((catalogPreview && "title" in catalogPreview
                    ? catalogPreview.title
                    : undefined) ?? previewItem.contentAssetId)}
          </span>
          <IconButton
            onClick={() => setPreviewItem(null)}
            aria-label="Close preview"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "grid",
            placeItems: "center",
            bgcolor: "black",
            p: previewItem && !previewItem.assetUrl ? 2 : 0,
            minHeight: 240,
          }}
        >
          {!previewItem ? null : !previewItem.assetUrl ? (
            // A catalogue title - the real clip lives behind `contentAssetId`
            // and is resolved server-side, same player as the channel-level
            // "preview before scheduling" lightbox.
            <SignagePreviewPlayer
              preview={catalogPreview}
              loading={catalogPreviewLoading}
              error={catalogPreviewError}
              emptyText="This clip has nothing to play."
            />
          ) : previewItem.kind === "video" ? (
            <Box
              component="video"
              data-testid="item-preview-video"
              src={previewItem.assetUrl}
              controls
              autoPlay
              sx={{ width: "100%", maxHeight: "70vh" }}
            />
          ) : (
            <Box
              component="img"
              data-testid="item-preview-image"
              src={previewItem.assetUrl}
              alt=""
              sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }}
            />
          )}
        </DialogContent>
      </Dialog>
      <WebPageDialog
        open={webOpen}
        onClose={() => setWebOpen(false)}
        onAdd={addWebPage}
      />
      <ChannelPickerDialog
        open={channelOpen}
        onClose={() => setChannelOpen(false)}
        channels={channels}
        onPick={addChannel}
      />
      <SlidePickerDialog
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        slides={slides}
        onPick={addSlide}
      />
    </Dialog>
  );
}

/**
 * Pick a live TV channel to drop into a loop.
 *
 * A CAROUSEL rather than a wrapping grid, for the same reason the schedule
 * builder's Live TV tab is one: this is the line-up the location was SOLD, in
 * the order its operator already arranged on the Channels page. Wrapping it
 * into rows destroys that order visually - position four lands underneath
 * position one - and the sequence is the only thing that makes a long line-up
 * navigable. One horizontal band keeps it intact.
 *
 * There is no "not sold" state here: the button that opens this dialog does not
 * exist unless there is something behind it, so an operator never arrives to be
 * told no.
 */
function ChannelPickerDialog({
  open,
  onClose,
  channels,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  channels: SignageChannelOption[];
  onPick: (c: SignageChannelOption) => void;
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.providerName ?? "").toLowerCase().includes(q)
    );
  }, [channels, query]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Add a live TV channel</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <TextField
            size="small"
            placeholder="Search channels…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ maxWidth: 280 }}
          />

          {shown.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              {query
                ? `Nothing matches “${query}”.`
                : "No channels are available here."}
            </Typography>
          ) : (
            /*
             * ROWS, not the card strip this shipped as.
             *
             * Cards with a large logo plate are right for BROWSING a catalogue.
             * This is a picker sitting on top of the playlist editor, whose own
             * items are rows of exactly this shape - so rows read as "the thing
             * you are about to add", and a strip of big tiles read as a
             * different product bolted on. They also survive the real range: a
             * hotel with four channels does not get a mostly-empty band, and
             * one with forty gets a list rather than a horizontal scroll nobody
             * finds the end of.
             */
            <Stack
              gap={0.5}
              sx={{
                maxHeight: 320,
                overflowY: "auto",
                // Scrolls inside itself; the dialog body never does.
                mx: -1,
                px: 1,
              }}
            >
              {shown.map((c) => (
                <Stack
                  key={c.contentAssetId}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  role="button"
                  tabIndex={0}
                  aria-label={`Add ${c.name} to the playlist`}
                  onClick={() => onPick(c)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onPick(c);
                    }
                  }}
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    border: `1px solid ${tokens.divider}`,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: tokens.brand,
                      bgcolor: tokens.rowAlt,
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${tokens.brand}`,
                      outlineOffset: 1,
                    },
                  }}
                >
                  <ChannelMark
                    src={c.logoUrl}
                    name={c.name}
                    id={c.contentAssetId}
                    size={40}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}
                    >
                      {c.name}
                    </Typography>
                    {/* Only when there is one. The catalogue says "Unknown" for
                        unattributed channels, and the server drops it rather
                        than printing the word under a channel the operator
                        recognises perfectly well. */}
                    {c.providerName && (
                      <Typography
                        noWrap
                        sx={{ fontSize: 12, color: tokens.inkMuted }}
                      >
                        {c.providerName}
                      </Typography>
                    )}
                  </Box>
                  <AddIcon sx={{ fontSize: 18, color: tokens.inkFaint }} />
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Ask for a web page, and check it can actually be shown before accepting it.
 *
 * ## Why this is not just a text field
 *
 * A page that refuses to be framed (`X-Frame-Options`, CSP `frame-ancestors`)
 * renders on a panel as a WHITE BOX. No error fires, nothing reaches the
 * failed-item counter, and the screen still reports itself as playing - so the
 * operator's first evidence is somebody standing in the lobby looking at a
 * white rectangle. A large share of the pages anyone reaches for first refuse
 * framing, so this is the common case, not the edge.
 *
 * The probe runs server-side while the operator is still looking at the form,
 * which is the only moment the answer is actionable.
 *
 * ## It advises; it does not refuse
 *
 * `unknown` is a real answer - a site can be briefly down, rate-limit us, or
 * simply be reachable from the hotel's network and not from ours - and Add
 * stays enabled through all of it. The only thing that BLOCKS is an address the
 * server would not point a screen at at all (a bad scheme, a private host),
 * which the save routes refuse independently.
 */
function WebPageDialog({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (url: string) => void;
}) {
  const [url, setUrl] = useState("");
  const [probe, setProbe] = useState<SignageUrlProbe | null>(null);
  const [checking, setChecking] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUrl("");
    setProbe(null);
    setChecking(false);
    setRejected(null);
  }, [open]);

  const check = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setChecking(true);
    setProbe(null);
    setRejected(null);
    try {
      setProbe(await foyerApi.probeSignageUrl(trimmed));
    } catch {
      // A 400 here means the ADDRESS is refused, not that the site is down -
      // the two are worth different words, so this one names the field rather
      // than reporting on the page.
      setRejected("That is not an address a screen can be pointed at.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Add a web page</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <TextField
            autoFocus
            size="small"
            label="Address"
            placeholder="https://example.com/lobby"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            // On blur rather than per keystroke: this is a real outbound
            // request to somebody else's server, and one per character typed
            // would be a small denial-of-service run from our address.
            onBlur={(e) => void check(e.target.value)}
            error={Boolean(rejected)}
            helperText={
              rejected ??
              "The page is shown full-screen for as long as its slot lasts."
            }
          />

          {checking && (
            <Stack direction="row" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                Checking the page…
              </Typography>
            </Stack>
          )}

          {probe?.embeddable === "no" && (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              This site refuses to be shown inside another page
              {probe.reason ? ` (${probe.reason})` : ""}. On a screen it would
              appear as a blank white rectangle, with nothing to say why. Use a
              page you control, or one that allows embedding.
            </Alert>
          )}
          {probe?.embeddable === "unknown" && (
            <Alert severity="info" sx={{ fontSize: 12 }}>
              We could not reach this page from here, so we cannot say whether
              it will appear. That is not necessarily a problem — a page on the
              property&apos;s own network is reachable by the screen and not by
              us. Check it on a screen before relying on it.
            </Alert>
          )}
          {probe?.embeddable === "yes" && (
            <Alert severity="success" sx={{ fontSize: 12 }}>
              This page can be shown on a screen.
              {probe.finalUrl ? ` It redirects to ${probe.finalUrl}.` : ""}
            </Alert>
          )}
          {probe?.insecure && (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              This is an <code>http://</code> address. A screen loads Foyer over
              https and will block it as insecure content — use https if the
              site offers it.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {/* Enabled whatever the probe said. The probe is advice: the operator
            can see something we cannot, and a warning they have read and
            accepted is not a reason to stop them. */}
        <Button
          variant="contained"
          disabled={!url.trim() || checking}
          onClick={() => onAdd(url)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Pick a filled-in slide to drop into a loop.
 *
 * Rows, matching the channel picker and the editor behind it. A slide has no
 * artwork of its own to browse - it is a name somebody chose so they could find
 * it again - so a card grid would be a lot of empty boxes.
 */
function SlidePickerDialog({
  open,
  onClose,
  slides,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  slides: SignageTemplateInstance[];
  onPick: (s: SignageTemplateInstance) => void;
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? slides.filter((s) => s.name.toLowerCase().includes(q)) : slides;
  }, [slides, query]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Add a slide</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <TextField
            size="small"
            placeholder="Search slides…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ maxWidth: 280 }}
          />
          {shown.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              {query ? `Nothing matches “${query}”.` : "No slides here yet."}
            </Typography>
          ) : (
            <Stack
              gap={0.5}
              sx={{ maxHeight: 320, overflowY: "auto", mx: -1, px: 1 }}
            >
              {shown.map((s) => (
                <Stack
                  key={s.id}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  role="button"
                  tabIndex={0}
                  aria-label={`Add ${s.name} to the playlist`}
                  onClick={() => onPick(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onPick(s);
                    }
                  }}
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    border: `1px solid ${tokens.divider}`,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: tokens.brand,
                      bgcolor: tokens.rowAlt,
                    },
                  }}
                >
                  <SlideIcon sx={{ fontSize: 20, color: tokens.inkFaint }} />
                  <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                    {s.name}
                  </Typography>
                  <AddIcon sx={{ fontSize: 18, color: tokens.inkFaint }} />
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
